// The Storefront Overview: the week's numbers for one assistant or all of them, one market or all,
// a list of assistants and markets where a row focuses the page on it, what shoppers ask (topics
// Chat found in first questions) and where conversations start. A store with one site and one
// language shows only its numbers, topics and pages: there is nothing to compare.
import { useState } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { WorkspaceIcon } from '../icons';
import { ErrorState, LoadingState } from '../primitives';
import {
  useStorefrontApi,
  type OverviewRow,
  type OverviewScope,
  type StoreMarket,
  type StoreSummary,
} from './api';
import { Kpis } from './conversations-tab';
import { creditNumber, percent } from './format';
import { storeLine, storeState } from './store-line';
import { useRemote } from './use-storefront';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;

interface TableRow {
  key: string;
  store: string;
  market: string | null;
  name: string;
  sub: string;
  nested: boolean;
  totals: Omit<OverviewRow, 'store' | 'market'>;
  isAssistant: boolean;
}

export function OverviewTab({
  scope,
  stores,
  onFocus,
}: {
  scope: OverviewScope;
  stores: StoreSummary[];
  onFocus: (next: OverviewScope) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const api = useStorefrontApi();
  const locale = useAppCatalogLocale() ?? 'en';
  // Assistants nobody has talked to this week fold into one line under the busy ones.
  const [showIdle, setShowIdle] = useState(false);
  const overview = useRemote(
    () => api.overview(scope, 7, locale),
    `${scope.store}:${scope.market}:${locale}:${stores.map((store) => store.id).join(',')}`,
  );
  if (overview.error && !overview.data)
    return <ErrorState message={copy.overviewFailed} onRetry={overview.refresh} />;
  if (!overview.data) return <LoadingState label={copy.loading} />;
  const data = overview.data;
  const rows = tableRows(data.rows, scope, stores, copy);
  const idle = new Set(
    scope.store === 'all'
      ? rows
          .filter((row) => row.isAssistant && row.totals.conversations === 0)
          .map((row) => row.store)
      : [],
  );
  const fold = idle.size > 1 && !showIdle;
  const shownRows = fold ? rows.filter((row) => !idle.has(row.store)) : rows;
  const share = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 });
  const topics = data.topics ?? [];
  const startPages = data.startPages ?? [];
  const topTopic = Math.max(1, ...topics.map((topic) => topic.share));
  const title =
    scope.store !== 'all'
      ? copy.overviewMarketsTitle
      : rows.some((row) => !row.isAssistant)
        ? copy.overviewAssistantsTitle
        : copy.overviewAssistantsOnly;
  return (
    // While another scope loads, the last one stays in place, dimmed, so nothing jumps.
    <div
      className="sf-overview"
      data-testid="sf-overview"
      aria-busy={overview.loading || undefined}
    >
      <Kpis stats={data} />
      {rows.length > 1 && (
        <section className="sf-ov-card" aria-label={title}>
          <header className="sf-ov-head">
            <h2>{title}</h2>
          </header>
          <div className="sf-ov-row sf-ov-labels" aria-hidden="true">
            <span />
            <span />
            <span>{copy.colConversations}</span>
            <span>{copy.colCart}</span>
            <span>{copy.colCheckout}</span>
            <span>{copy.colPerson}</span>
            <span>{copy.colCost}</span>
          </div>
          <ul className="sf-ov-list">
            {shownRows.map((row) => {
              // Under a market filter, an assistant's row keeps that market when it is picked.
              const target: OverviewScope = {
                store: row.store,
                market: row.market ?? scope.market,
              };
              const focused = row.store === scope.store && target.market === scope.market;
              // An assistant nobody has talked to yet: a dash rather than a row of zeros.
              const idle = row.totals.conversations === 0;
              const cells = {
                conversations: new Intl.NumberFormat(locale).format(row.totals.conversations),
                cart: idle
                  ? '—'
                  : percent(row.totals.addedToCart, row.totals.conversations, locale),
                checkout: idle
                  ? '—'
                  : percent(row.totals.reachedCheckout, row.totals.conversations, locale),
                person: idle ? '—' : String(row.totals.askedForPerson),
                cost: idle ? '—' : creditNumber(row.totals.costCreditMicros, locale),
              };
              return (
                <li key={row.key}>
                  <button
                    type="button"
                    className="sf-ov-row"
                    data-nested={row.nested || undefined}
                    data-assistant={row.isAssistant || undefined}
                    aria-pressed={focused}
                    aria-label={formatCopy(copy.rowAria, {
                      name: row.name,
                      sub: row.sub,
                      ...cells,
                    })}
                    data-testid="sf-ov-row"
                    onClick={() => onFocus(target)}
                  >
                    <span className="sf-ov-name">
                      {row.market && !row.isAssistant && (
                        <span className="sf-cc">{row.market.toUpperCase()}</span>
                      )}
                      <span>
                        <b>{row.name}</b>
                        <small>{row.sub}</small>
                      </span>
                    </span>
                    <span className="sf-ov-trend">
                      {!idle && <Trend days={row.totals.byDay} />}
                    </span>
                    <span>{cells.conversations}</span>
                    <span className="sf-ov-col">{cells.cart}</span>
                    <span className="sf-ov-col">{cells.checkout}</span>
                    <span className="sf-ov-col">{cells.person}</span>
                    <span>{cells.cost}</span>
                  </button>
                </li>
              );
            })}
            {fold && (
              <li>
                <button
                  type="button"
                  className="sf-ov-more"
                  data-testid="sf-ov-idle"
                  onClick={() => setShowIdle(true)}
                >
                  {formatCopy(copy.overviewIdle, { count: String(idle.size) })}
                  <WorkspaceIcon name="chevron" size={14} />
                </button>
              </li>
            )}
          </ul>
        </section>
      )}
      {topics.length || startPages.length ? (
        <div
          className="sf-ov-duo"
          data-single={!(topics.length && startPages.length > 1) || undefined}
        >
          {topics.length ? (
            <section className="sf-ov-card" aria-label={copy.topicsTitle} data-testid="sf-topics">
              <header className="sf-ov-head">
                <h2>{copy.topicsTitle}</h2>
                <span>{copy.topicsNote}</span>
              </header>
              <div className="sf-topics">
                {topics.map((topic) => (
                  <div key={topic.name} className="sf-topic">
                    <span className="sf-topic-line">
                      <b>{topic.name}</b>
                      <span>{share.format(topic.share / 100)}</span>
                    </span>
                    {/* Bars against the biggest topic, so their lengths compare like their shares. */}
                    <span className="sf-share" aria-hidden="true">
                      <i style={{ width: `${(topic.share / topTopic) * 100}%` }} />
                    </span>
                    <span className="sf-topic-example">“{topic.example}”</span>
                  </div>
                ))}
              </div>
              {startPages.length === 1 && (
                <p className="sf-ov-foot" data-testid="sf-start-one">
                  {formatCopy(copy.startPagesOne, {
                    page: copy[`startPage.${startPages[0].kind}` as StorefrontCopyKey],
                  })}
                </p>
              )}
            </section>
          ) : null}
          {/* One kind of start page is a sentence, not a chart. */}
          {startPages.length > 1 || (startPages.length === 1 && !topics.length) ? (
            <section
              className="sf-ov-card"
              aria-label={copy.startPagesTitle}
              data-testid="sf-start-pages"
            >
              <header className="sf-ov-head">
                <h2>{copy.startPagesTitle}</h2>
              </header>
              {startPages.map((page) => (
                <div key={page.kind} className="sf-start-page">
                  <span>{copy[`startPage.${page.kind}` as StorefrontCopyKey]}</span>
                  <span className="sf-share" aria-hidden="true">
                    <i style={{ width: `${page.share}%` }} />
                  </span>
                  <span>{share.format(page.share / 100)}</span>
                </div>
              ))}
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The rows: across all assistants, each assistant then its markets; inside one, its markets. The
 * busiest come first (conversations in the period, then name), so the assistants that matter this
 * week lead and ones nobody used yet sit at the end.
 */
function tableRows(
  rows: OverviewRow[],
  scope: OverviewScope,
  stores: StoreSummary[],
  copy: Copy,
): TableRow[] {
  const byStore = new Map<string, OverviewRow[]>();
  for (const row of rows) byStore.set(row.store, [...(byStore.get(row.store) ?? []), row]);
  const groups = stores
    .flatMap((store) => {
      const own = byStore.get(store.id);
      return own?.length ? [{ store, own, totals: sum(own) }] : [];
    })
    .sort(
      (a, b) =>
        b.totals.conversations - a.totals.conversations || a.store.name.localeCompare(b.store.name),
    );
  const out: TableRow[] = [];
  for (const { store, own, totals } of groups) {
    const markets = own
      .filter((row) => row.market)
      .sort((a, b) => b.conversations - a.conversations);
    const marketRows = markets.map((row) => {
      const info = store.markets?.find((market) => market.code === row.market);
      return {
        key: `${store.id}:${row.market}`,
        store: store.id,
        market: row.market,
        name: info?.name ?? (row.market ?? '').toUpperCase(),
        sub: info ? marketSub(info) : store.domain,
        nested: scope.store === 'all',
        totals: row,
        isAssistant: false,
      } satisfies TableRow;
    });
    if (scope.store === 'all') {
      const version = store.live
        ? formatCopy(copy.rowVersion, { version: String(store.live.version) })
        : null;
      out.push({
        key: store.id,
        store: store.id,
        market: null,
        name: store.name,
        // Like the store menu: the site (said once), how far the assistant is, its published version.
        sub:
          markets.length > 1
            ? [
                formatCopy(copy.rowMarkets, { count: String(markets.length) }),
                storeState(store, copy),
                version,
              ]
                .filter(Boolean)
                .join(' · ')
            : storeLine(store, copy, [version]),
        nested: false,
        totals,
        isAssistant: true,
      });
      if (markets.length > 1) out.push(...marketRows);
    } else if (store.id === scope.store) {
      out.push(...marketRows);
    }
  }
  return out;
}

const marketSub = (market: StoreMarket) => `${market.site} · ${market.currency}`;

function sum(rows: OverviewRow[]): TableRow['totals'] {
  const days = new Map<string, number>();
  for (const row of rows)
    for (const day of row.byDay) days.set(day.date, (days.get(day.date) ?? 0) + day.count);
  const add = (
    key:
      | 'conversations'
      | 'previous'
      | 'addedToCart'
      | 'reachedCheckout'
      | 'askedForPerson'
      | 'costCreditMicros',
  ) => rows.reduce((total, row) => total + row[key], 0);
  return {
    conversations: add('conversations'),
    previous: add('previous'),
    addedToCart: add('addedToCart'),
    reachedCheckout: add('reachedCheckout'),
    askedForPerson: add('askedForPerson'),
    costCreditMicros: add('costCreditMicros'),
    byDay: [...days.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count })),
  };
}

/** A row's week as a thin line, the last day marked. */
function Trend({ days }: { days: { count: number }[] }) {
  if (days.length < 2) return null;
  const counts = days.map((day) => day.count);
  const low = Math.min(...counts);
  const high = Math.max(...counts);
  const y = (value: number) => (high === low ? 10 : 17 - ((value - low) / (high - low)) * 14);
  const step = 56 / (counts.length - 1);
  const points = counts.map(
    (value, index) => `${(2 + index * step).toFixed(1)},${y(value).toFixed(1)}`,
  );
  const [lastX, lastY] = points[points.length - 1].split(',');
  return (
    <svg className="sf-trend" viewBox="0 0 60 20" width="60" height="20" aria-hidden="true">
      <polyline points={points.join(' ')} />
      <circle cx={lastX} cy={lastY} r="2.4" />
    </svg>
  );
}
